import React from 'react'
import ReactDOM from 'react-dom/client'
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType
} from 'react-router-dom'
import { Toaster, ToastBar } from 'react-hot-toast'
import { clarity } from 'react-microsoft-clarity'
import { Toast as ArtemToast } from '@/common'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import ErrorPage from './ErrorPage'
import '@/i18n'
import '@/styles/index.css'
import Root from './Root'
import * as Sentry from '@sentry/react'
import { CLARITY_USER_ID_STORAGE_KEY } from '@/features/auth/hooks/useAuth'

if (import.meta.env.VITE_CLARITY_PROJECT_ID) {
  clarity.init(import.meta.env.VITE_CLARITY_PROJECT_ID!)
  // Identify with stored user ID immediately so Clarity ties this session to the user
  // before the session API returns (avoids one "anonymous" user per session)
  const storedUserId = sessionStorage.getItem(CLARITY_USER_ID_STORAGE_KEY)
  if (storedUserId && clarity.hasStarted()) {
    clarity.identify(storedUserId, {})
  }
}

function initSentry() {
  const enableReplay = import.meta.env.VITE_SENTRY_REPLAY_ENABLED === 'true'

  const integrations = [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    })
  ]

  if (enableReplay) {
    integrations.push(Sentry.replayIntegration())
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN!,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT!,
    integrations,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0
  })
}

initSentry()

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)
const router = sentryCreateBrowserRouter([
  {
    path: '/*',
    element: <Root />,
    errorElement: <ErrorPage />
  }
])

const queryClient = new QueryClient()

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Toaster position="top-right">
      {t => (
        <ToastBar style={{ padding: 0, boxShadow: 'none' }} toast={t}>
          {({ message }) => <ArtemToast message={message} type={t.type} />}
        </ToastBar>
      )}
    </Toaster>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </React.StrictMode>
)
